import { memo, useMemo } from 'react'
import { renderAgentMarkdown } from '@/components/agent-markdown/renderAgentMarkdown'
import type { GeneralChatMessage } from '../../types'
import type { ChatCard, ThreadLoad } from './cardResults'
import { CardBody } from './ChatCardView'
import { mergeTurnCards } from './turns'
import { UpdatedBelowProvider } from './updatedBelow'

const NO_KEYS: ReadonlySet<string> = new Set()

/** The agent's mark beside its words: the ✦ tile of the invoicing chat (v17). */
export const AgentAvatar = () => (
    <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-[9px] bg-accent-soft text-sm text-accent">
        ✦
    </span>
)

/** The time a block was last drawn, as the chat shows it: "as of 10:14". */
const asOfWords = (iso: string): string => {
    const at = new Date(iso)
    return Number.isNaN(at.getTime()) ? '' : `as of ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
}

interface AnswerBlockProps {
    /** The cards one agent turn drew, oldest first. */
    cards: ChatCard[]
    /** The agent's words that follow the cards in the same turn: the block's one-line lead. */
    replies: GeneralChatMessage[]
    /** When the block's newest card was stored. */
    asOf: string
    loads: ThreadLoad[]
    /** The loads a newer block shows in a different state: their rows here are dimmed and say so. */
    updatedBelow?: ReadonlySet<string>
    /** The user's message the turn answers. */
    question?: string
    onSend: (message: string) => void
}

/**
 * One agent turn as one answer block (v17): the ✦ mark, the agent's words as a short lead, then the body its cards drew, with each
 * load once (the newest read wins), and a footer with the time the answer was read. The reply has no card surface.
 */
export const AnswerBlock = memo(function AnswerBlock({ cards, replies, asOf, loads, updatedBelow, question, onSend }: AnswerBlockProps) {
    const merged = useMemo(() => mergeTurnCards(cards), [cards])
    const lead = replies.map((reply) => reply.content ?? '').join('\n\n')
    return (
        <UpdatedBelowProvider keys={updatedBelow ?? NO_KEYS}>
            <section aria-label="Agent answer" className="flex w-full min-w-0 max-w-[95%] gap-3 self-start text-foreground">
                <AgentAvatar />
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                    {lead && (
                        // The lead is at most two sentences (the agent's prompt), so it is shown whole: no clamp, no "more".
                        <div className="pt-0.5 text-sm">{renderAgentMarkdown(lead, { links: true })}</div>
                    )}
                    {merged.map((card) => (
                        <div key={card.id} className="min-w-0">
                            <CardBody card={card} loads={loads} question={question} onSend={onSend} />
                        </div>
                    ))}
                    {asOfWords(asOf) && <footer className="text-xs text-foreground-muted">{asOfWords(asOf)}</footer>}
                </div>
            </section>
        </UpdatedBelowProvider>
    )
})
